import { useEffect, useRef } from 'react';

interface WindowSize {
	width: number;
	height: number;
}

interface WindowResizeCallback {
	current: WindowSize;
	previous: WindowSize;
}

/**
 * Hook to listen for window resize events and trigger a callback function
 *
 * @param callback Function to call when window is resized, receives current and previous window dimensions
 * @param deps Optional dependency array for the callback function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   useWindowResize(({ current, previous }) => {
 *     console.log(`Window resized from ${previous.width} to ${current.width}`);
 *
 *     if (current.width < 768 && previous.width >= 768) {
 *       // Just crossed mobile breakpoint
 *       sideBarStore.setCollapsed(true);
 *     } else if (current.width >= 768 && previous.width < 768) {
 *       // Just crossed desktop breakpoint
 *       sideBarStore.setCollapsed(false);
 *     }
 *   });
 *
 *   return <div>My component</div>;
 * }
 * ```
 */
export function useWindowResize(
	callback: (resizeInfo: WindowResizeCallback) => void,
	deps: React.DependencyList = []
): void {
	const callbackRef = useRef(callback);
	const previousSizeRef = useRef<WindowSize>({
		width: typeof window !== 'undefined' ? window.innerWidth : 0,
		height: typeof window !== 'undefined' ? window.innerHeight : 0,
	});

	// Keep callback ref up to date
	callbackRef.current = callback;

	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		const handleResize = () => {
			const currentSize: WindowSize = {
				width: window.innerWidth,
				height: window.innerHeight,
			};

			const previousSize = previousSizeRef.current;

			callbackRef.current({
				current: currentSize,
				previous: previousSize,
			});

			// Update previous size for next call
			previousSizeRef.current = currentSize;
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, deps);
}
